import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Wallet } from "lucide-react";
import { useBalance } from "@/features/subscription/payment-queries";
import { useAccountUsage } from "@/features/dashboard/queries";
import { usePermissions } from "@/hooks/use-permissions";
import { AddBalance as AddBalanceDialog } from "@/features/subscription/components/add-balance";
import { SubscriptionPlanId } from "@/features/subscription/types";
import { deriveCredits, formatCredits } from "@/features/subscription/hooks/use-credits";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";

/** Below sm every top-bar action is the same borderless 40px icon button with a 44px hit area; these override a pill's size, border, fill and colour there. */
export const topbarPhoneGhostClasses =
  "max-sm:relative max-sm:h-10 max-sm:w-10 max-sm:min-h-10 max-sm:min-w-10 max-sm:pointer-coarse:h-10 max-sm:pointer-coarse:w-10 max-sm:pointer-coarse:min-h-10 max-sm:pointer-coarse:min-w-10 max-sm:before:absolute max-sm:before:-inset-0.5 max-sm:rounded-lg max-sm:border-0 max-sm:bg-transparent max-sm:p-0 max-sm:text-muted-foreground max-sm:hover:bg-muted max-sm:hover:text-foreground max-sm:[&_svg]:h-4 max-sm:[&_svg]:w-4 max-sm:[&_svg]:text-current";

export const topbarPillBaseClasses =
  `inline-flex min-h-10 min-w-10 pointer-coarse:min-h-11 pointer-coarse:min-w-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${topbarPhoneGhostClasses}`;

interface BalanceButtonProps {
  /** Attribution for the AddBalance dialog and the funnel events. */
  source?: string;
}

export function BalanceButton({ source = "topbar_balance_pill" }: BalanceButtonProps = {}) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: balanceData, isLoading: balanceLoading } = useBalance();
  const { data: subscription } = useAccountUsage();
  const { canManageBilling } = usePermissions();
  const { creditMode, grantRemaining, remaining, isLow } = deriveCredits(subscription);

  const planId = subscription?.planId;
  const isTrial = planId === SubscriptionPlanId.Speak_Free_Trial;
  const isPayg = planId === SubscriptionPlanId.Speak_Pay_As_You_Go;
  const currentBalance = balanceData?.balance ?? 0;
  const visible = !!subscription && canManageBilling && (isTrial || isPayg || creditMode);
  const impressionFired = useRef(false);

  // Waits for the balance query so has_balance is accurate, and latches so a later
  // balance change cannot double-count one pill view.
  useEffect(() => {
    if (!visible || balanceLoading || impressionFired.current) return;
    impressionFired.current = true;
    trackFunnel("powerspot", "balance_pill_impression", {
      source,
      has_balance: currentBalance > 0,
    });
  }, [visible, balanceLoading, source, currentBalance]);

  if (!visible) return null;

  function handleClick() {
    trackFunnel("powerspot", "balance_pill_clicked", { source });
    pushDataLayer("Balance", "Balance Pill Clicked", source);
    setDialogOpen(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`${topbarPillBaseClasses} ${isLow
          ? "border-danger/40 bg-danger/10 text-danger hover:bg-danger/15"
          : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        aria-label={
          isLow
            ? t("TOPBAR.ADD_BALANCE_LOW", {
              credits: formatCredits(remaining),
              defaultValue: "{{credits}} left. Top up",
            })
            : t("TOPBAR.ADD_BALANCE", { defaultValue: "Top up" })
        }
        data-testid="topbar-balance-pill"
        data-low={isLow ? "true" : undefined}
      >
        {isLow ? (
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <Wallet className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}
        <span data-sentry-mask className={`font-medium ${isLow ? "text-danger" : "text-foreground"}`}>
          {formatCredits(creditMode ? remaining : currentBalance)}
        </span>
        <span className={`hidden xl:inline ${isLow ? "text-danger" : "text-muted-foreground"}`}>
          · {t("ACTION.TOP_UP", "Top up")}
        </span>
      </button>
      <AddBalanceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        source={source}
        canManageBilling={canManageBilling}
      />
    </>
  );
}
